import React from 'react';

import { DeviceActionProps } from './DeviceActions';
import { ListItemIcon } from '@mui/material';
import { RemoteDesktopIcon } from '../../../../../../theme/icons';
import { UtilInstance } from '../../../../../../util/Util';
import { getPolyglot } from '../../../../../../i18n';
import { useAppSelector } from '../../../../../../redux/store.model';
import {
  ButtonIcon,
  MenuItemPrimary,
} from '../../../../../../theme/components';
import DeviceModel from '../../../../../../redux/commander/api/DeviceModel';
import useCommanderStatus from '../../../../../../redux/commander/commander-status/hooks/useCommanderStatus';
import { useDeviceRemoteDesktopSupport } from '../../../../../../redux/commander/device-configuration/hooks';
import { RemoteDesktopStatus } from '../../../../../../redux/commander/device-configuration/api/adapter/DeviceRemoteDesktopAdapter';
import { getFeatureToggle } from '../../../../../../featureToggle';

const iconId = 'interface';
const icon = <RemoteDesktopIcon height={'1em'} />;
const iconGrid = <RemoteDesktopIcon height={'1em'} />;

const wsProxyURL = UtilInstance.getRemoteDesktopProxyPath();
const deviceConfigURL = UtilInstance.getDeviceConfigServicePath();
const remoteDesktopURL = UtilInstance.getRemoteDesktopWindowURL();

export function DeviceRemoteDesktopAction(props: DeviceActionProps) {
  const { device, groupId, variant } = props;
  const polyglot = getPolyglot();
  const featureToggles = useAppSelector(getFeatureToggle);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const tooltipContent = polyglot.t('device.tooltip.open_remote_desktop');

  const openRemoteDesktopWindow = (e: React.MouseEvent) => {
    const commanderDesktop = async (
      _deviceUuid: string,
      _accessToken: string
    ) => {
      // There is currently no official way to open a new window and applying
      // the authorization header to it. For this reason I add it to the
      // query parameters.
      let addParam = '';
      if (!deviceConfigURL.startsWith('/api/config')) {
        addParam += '&config=' + encodeURIComponent(deviceConfigURL);
      }

      if (!wsProxyURL.startsWith('/api/remote')) {
        addParam += '&wsproxy=' + encodeURIComponent(wsProxyURL);
      }
      const url =
        remoteDesktopURL +
        '?id=' +
        encodeURIComponent(_deviceUuid) +
        '&sso=' +
        encodeURIComponent(_accessToken) +
        addParam;
      window
        ?.open(
          url,
          'Cloud-Remote Desktop',
          'popup=true,innerWidth=800,innerHeight=480,resizable,scrollbars=yes,status=false,location=false,toolbar=false'
        )
        ?.focus();
    };
    commanderDesktop(device.uuid, accessToken);
  };

  // Device status
  const { commanderStatus, isLoading: loadingCommanderStatus } =
    useCommanderStatus(device.uuid);
  const cloudConnectivityStatus = new DeviceModel().cloudConnectivityStatus(
    device?.lastPing,
    commanderStatus
  );

  // 1. The feature for remote desktop access must be enabled
  //    This is must be enabled in the build as well as in the
  //    main workspace of the current user.
  if (!featureToggles.remoteDesktopAccess) {
    return null;
  }

  // 2. User needs the right "remoteDesktopUsage"
  // (This is checked during login in useWorkspaceModules hook)

  // 3. Device must support the feature
  // Check device capability via device config service API
  // Endpoint: GET /remotecontrol/{deviceID}/available
  const { status: remoteDesktopStatus, isLoading: loadingDeviceCapability } =
    useDeviceRemoteDesktopSupport(device.uuid);

  // Hide button entirely if device does not support remote desktop
  if (remoteDesktopStatus === RemoteDesktopStatus.NOT_SUPPORTED) {
    return null;
  }

  // Determine if button should be enabled:
  // - Status must be SUPPORTED_ENABLED
  // - VPN must be connected
  const isEnabled =
    remoteDesktopStatus === RemoteDesktopStatus.SUPPORTED_ENABLED &&
    cloudConnectivityStatus.cloudConnectivityVPN;

  return variant === 'menuItem' ? (
    <MenuItemPrimary
      onClick={openRemoteDesktopWindow}
      className={`icon-${iconId} group-${groupId}`}
      disabled={!isEnabled}
    >
      <ListItemIcon sx={{ color: 'inherit' }}>{iconGrid}</ListItemIcon>
      {tooltipContent}
    </MenuItemPrimary>
  ) : (
    <ButtonIcon
      className={`icon-${iconId} group-${groupId}`}
      tooltip={tooltipContent}
      onClick={openRemoteDesktopWindow}
      disabled={!isEnabled}
      loading={
        device.loadingData || loadingCommanderStatus || loadingDeviceCapability
      }
      sx={{ width: '45px', height: '45px' }}
    >
      {icon}
    </ButtonIcon>
  );
}

export default DeviceRemoteDesktopAction;
